import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import WarningModal from "components/DeleteModal/WarningModal";
import { EntityCouponDialogForm } from "components/FormDialog/EntityCouponDialogForm";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaCalendarCheck, FaTicketAlt } from "react-icons/fa";
import { createCoupons, deleteCoupons, getAllCoupons } from "redux/features/Admin/Stripe/stripeSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";

export interface CouponsPageProps {
  className?: string;
}

interface ModalState {
  isCoupon: boolean;
  isDeleteModalOpen: boolean;
  selectedItem: any | null;
  loading: boolean;
}

interface Coupon {
  id: string;
  code: string;
  coupon: {
    id: string;
    amount_off?: number;
    max_redemptions?: number;
    times_redeemed?: number;
  };
}

const CouponsPage: FC<CouponsPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const coupons = useAppSelector((state) => state.stripe.coupons || { data: [], hasMore: false });

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isCoupon: false,
    isDeleteModalOpen: false,
    selectedItem: null,
  });
  const [data, setData] = useState<Coupon[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchCoupons = async () => {
      setIsSearchLoading(true);
      const res = await dispatch(getAllCoupons({ startAfter: "" }));
      setData(res.payload.data);
      setIsSearchLoading(false);
    };
    fetchCoupons();
  }, [dispatch]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      setData(
        coupons.data.filter((item: Coupon) => item.code.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
      );
    } else {
      setData(coupons.data);
    }
  }, [debouncedSearchTerm, coupons.data]);

  const loadMoreCoupons = async () => {
    if (!coupons.hasMore) return;
    const lastCoupon = coupons.data[coupons.data.length - 1];
    const lastCouponId = lastCoupon?.id;
    if (lastCouponId) {
      setLoadingMore(true);
      const res = await dispatch(getAllCoupons({ startAfter: lastCouponId }));
      setData((prev) => [...prev, ...res.payload.data]);
      setLoadingMore(false);
    } else {
      console.error("No valid last coupon ID found.");
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleAddCoupon = () => {
    setModalState({ ...modalState, isCoupon: true });
  };

  const handleDeleteModalOpen = (item: Coupon) => {
    setModalState({ ...modalState, selectedItem: item, isDeleteModalOpen: true });
  };

  const handleModalClose = () => {
    setModalState({
      ...modalState,
      isCoupon: false,
      isDeleteModalOpen: false,
      selectedItem: null,
    });
  };

  const handleConfirmDelete = async () => {
    if (!modalState.selectedItem) return;
    setModalState((prev) => ({ ...prev, loading: true }));
    await dispatch(deleteCoupons(modalState.selectedItem.coupon.id));
    setData((prevData) => prevData.filter((item) => item.coupon.id !== modalState.selectedItem.coupon.id));
    setModalState((prev) => ({ ...prev, isDeleteModalOpen: false, loading: false }));
  };

  const handleTableAction = (action: string, item: Coupon) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    }
  };

  const columns = [
    {
      key: "code",
      label: "Code",
      render: (item: Coupon) => <span className="align-middle">{item.code}</span>,
    },
    {
      key: "reduction",
      label: "Réduction (€)",
      render: (item: Coupon) => (
        <span className="align-middle">{((item.coupon?.amount_off || 0) / 100).toFixed(2)}</span>
      ),
    },
    {
      key: "max_utilisations",
      label: "Max utilisations",
      render: (item: Coupon) => <span className="align-middle">{item.coupon?.max_redemptions || "illimité"}</span>,
    },
    {
      key: "times_redeemed",
      label: "Nombre de fois utilisées",
      render: (item: Coupon) => <span className="align-middle">{item.coupon?.times_redeemed || 0}</span>,
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaTicketAlt className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste des Codes Promo
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <ButtonPrimary onClick={handleAddCoupon} className=" btn-primary-gradient" sizeClass="px-4 py-2">
                Ajouter coupon
              </ButtonPrimary>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search coupons"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {modalState.loading && !isSearchLoading && <Loading />}

          {!modalState.loading && (
            <>
              {modalState.isDeleteModalOpen && (
                <WarningModal
                  open={modalState.isDeleteModalOpen}
                  id={modalState.selectedItem?.code}
                  title={modalState.selectedItem?.code}
                  handleClose={() => setModalState({ ...modalState, isDeleteModalOpen: false })}
                  handleDelete={handleConfirmDelete}
                  isLoading={modalState.loading}
                />
              )}

              {modalState.isCoupon && (
                <EntityCouponDialogForm
                  open={modalState.isCoupon}
                  handleClose={handleModalClose}
                  isLoading={modalState.loading}
                  addfn={(payload: any) => dispatch(createCoupons(payload))}
                  addfnTitle={"Générer un coupon"}
                />
              )}

              <DynamicTable
                columns={columns}
                data={data}
                totalItems={data.length}
                currentPage={1}
                onPageChange={() => {}}
                onAction={handleTableAction}
                totalPages={1}
                availableActions={["delete"]}
              />

              {!modalState.loading && data.length > 0 && coupons.hasMore && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={loadMoreCoupons}
                    className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 ease-in-out ${
                      loadingMore ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Chargement...
                      </div>
                    ) : (
                      "Charger plus"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default CouponsPage;
